// Path: src/admin/LoginPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  Grid,
  Typography,
  Paper,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../AuthContext"; // <-- use context, not api directly

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const cleanMsg = (e) => {
  const raw =
    typeof e === "string"
      ? e
      : e?.message || e?.error || e?.response?.data?.message || "";
  const text = String(raw).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  return text || "Login failed. Please check your email and password.";
};

// Only allow relative, same-origin paths to avoid open redirects
const sanitizeNext = (n) => {
  if (!n || typeof n !== "string") return "/admin";
  try {
    // disallow protocol or domain
    if (/^https?:\/\//i.test(n)) return "/admin";
    // must start with slash
    if (!n.startsWith("/")) return "/admin";
    // optional: prevent redirect back to /login loop
    if (n.startsWith("/login")) return "/admin";
    return n;
  } catch {
    return "/admin";
  }
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get("next") || "/admin";
  const next = useMemo(() => sanitizeNext(rawNext), [rawNext]);

  const { user, login, loading: authLoading } = useAuth(); // <-- from context

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false); // UI-only; server controls cookie max-age
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // If already authenticated, bounce to dashboard immediately
  useEffect(() => {
    if (user?.email) {
      navigate(next, { replace: true });
    }
  }, [user, navigate, next]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedEmail = email.trim();
    if (!emailRegex.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }

    setSubmitting(true);
    try {
      // IMPORTANT: uses AuthContext.login so context updates immediately
      const res = await login({ email: trimmedEmail, password });
      if (!res?.ok) throw new Error("Could not verify session after login.");

      // Persist a tiny flag for UX; cookie is httpOnly on server
      if (remember) localStorage.setItem("rememberAdmin", "1");
      else localStorage.removeItem("rememberAdmin");

      navigate(next, { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Login error:", err);
      setError(cleanMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  // While AuthProvider is checking session, show a minimal, fast skeleton
  if (authLoading && !user) {
    return (
      <>
        <Helmet>
          <title>Admin Login — Shivansh Foundation</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Grid
          container
          component="main"
          sx={{
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#f5f5f5",
            px: 2,
          }}
        >
          <CircularProgress />
        </Grid>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Login — Shivansh Foundation</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Grid
        container
        component="main"
        sx={{
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#f5f5f5",
          px: 2,
        }}
      >
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              Admin Login
            </Typography>

            <Box component="form" noValidate onSubmit={onSubmit} sx={{ mt: 2, width: "100%" }}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ bgcolor: "#fff" }}
                type="email"
                inputProps={{ inputMode: "email", "aria-describedby": "login-help" }}
              />

              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPw ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ bgcolor: "#fff" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPw ? "Hide password" : "Show password"}
                        onClick={() => setShowPw((v) => !v)}
                        edge="end"
                      >
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Remember me"
                />
                <Link className="text-indigo-500" to="/request-reset-password">
                  Forgot Password
                </Link>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }} role="alert" aria-live="assertive">
                  {error}
                </Alert>
              )}

              <LoadingButton
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                loading={submitting}
                disabled={!email || !password || submitting}
                sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 700 }}
              >
                Login
              </LoadingButton>

              <Typography
                id="login-help"
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center" }}
              >
                You’ll be redirected to the admin dashboard after signing in.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
